<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.viewRoute')"
        :size="60"
        :submit="submit"
        @close="close">
        <div
            class="flex gap-2 h-full"
            :style="{ minWidth: minWidthScroll }">
            <div
                v-if="positions && positions.length"
                v-loading="loadingSelectNode"
                class="w-1/2">
                <div
                    v-for="(item, index) in positions"
                    :key="index">
                    <hts-select-node
                        v-model="positions[index].node"
                        v-model:name="positions[index].fullNameNode"
                        :node-type-id="item.nodeType"
                        :prop="item.nodeType + index"
                        :label="item.nodeTypeName"
                        :key-label="getKeyLabel(item.quoteEqual)"
                        :location-type="item.quoteEqual"
                        :businesses="item.businesses"
                        :key-sub-label="getKeySubLabel(item.quoteEqual)"
                        :disabled="disabled"
                        required
                        @change="(val) => handelChangeSelectNode(index, val)" />
                </div>
            </div>

            <div class="w-full h-full">
                <hts-map-autocomplete
                    v-if="!disabled"
                    :label="$t('labels.findPosition')"
                    :placeholder="$t('labels.nodeModule.search')"
                    @get-place-detail="handleChoosePosition" />

                <div class="w-full h-full mt-5">
                    <hts-map-route
                        ref="map"
                        center-marker
                        class="h-full"
                        :route="markers"
                        :route-separated="routes"
                        :draggable="false"
                        :show-location="showLocation"
                        :lat-lng-location-routes="CLatLngLocationRoutes"
                        @clickMarker="handleSelectLocation"
                        @updateDirections="updateDirections">
                        <template #top>
                            <div class="flex min-h-5 justify-end gap-3">
                                <hts-map-icon-location :store-key="keyMapStore" />
                            </div>
                        </template>
                    </hts-map-route>
                </div>
            </div>
        </div>
        <hts-modal
            :show="warningLocation.visible"
            :title="$t('labels.warning')"
            type="confirm"
            :is-show="false"
            :content="warningLocation.label"
            @confirm="warningLocation.visible = false"
            @hide="warningLocation.visible = false" />
    </hts-drawer>
</template>

<script lang="ts" setup>
import routeMap from '@/pages/trucker-[id]/route/ignore/route-map'
import { $lodash, $urlSystem } from '#imports'

import screen from '~/components/utils/screen'

const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const props = defineProps({
  disabled: {
    type: Boolean,
    dafault: false
  },
  keyMapStore: {
    type: String,
    default: 'system'
  }
})

const { disabled, keyMapStore } = toRefs(props)

const { markers, setMarkers, routes, setRoutes, showLocation, updateDirections, CLatLngLocationRoutes, setRoutingPositions } = routeMap(keyMapStore.value)

const { $disableReactive } = useNuxtApp()
const visible = ref(false)
const map = ref()
const positions = ref<Array<{ [key: string]: any }>>([])
const theSameItems = ref<Array<[key: number]>>([])
const loadingSelectNode = ref(false)

const nodePosition = reactive({
  lat: 10.7561542,
  lng: 106.6896784,
  placeId: '' as string | null | undefined
})

const close = () => {
  visible.value = false
  locationList.value = []
}

const open = async (positionData: Array<{ [key: string]: any }>, theSamePositions: Array<[key: number]>) => {
  positions.value = $disableReactive(positionData)
  theSameItems.value = theSamePositions
  await getAllNodeLocation()
  setRoutingPositions()
  createRoute(positionData)
  visible.value = true
}

const locationList = ref<Array<{ [key: string]: any }>>([])

const createRoute = (positionData: Array<{ [key: string]: any }>) => {
  setMarkers(positionData, {
    renderMarkerByIndexNumber: true
  })
  setRoutes(positionData, {
    renderMarkerByIndexNumber: true
  })
}

const getAllNodeLocation = async () => {
  const api = $urlSystem.node.location.getAll
  const data: any = await $api(api)
  const resData = data.data.value

  if (resData?.success) {
    const list = resData.data

    locationList.value = list.map((item: any) => {
      return {
        position: {
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lng)
        },
        isNewLocation: item.newLocation,
        icon: item.newLocation ? '/assets/new-location.png' : '/assets/location.png',
        data: item,
        title: item.locationName,
        nodeDetail: item.nodeDetail || []
      }
    })
  }
}

const handleSelectLocation = (val: any) => {
  if (disabled.value) {
    return
  }

  handelSetDataPosition(val.nodeDetail, val)
}

const warningLocation = reactive({
  visible: false,
  label: ''
})

interface IItemNode {
  businesses: { [key: string]: Array<string> }
  _id: string
  fullNameNode: string
  nodeType: { [key: string]: any }
}

const handelSetDataPosition = (itemNode: Array<IItemNode>, item: any) => {
  const itemIndexSetPosition = positions.value.findIndex((item) => !item.node)
  if (itemIndexSetPosition === -1) {
    return ''
  }
  const itemSetPosition = positions.value[itemIndexSetPosition]
  if (!Array.isArray(itemNode) || itemNode.length < 1) {
    warningLocation.visible = true
    warningLocation.label = `Địa điểm ${item.title || ''} không thuộc loại điểm ${itemSetPosition.nodeTypeName || ''}`
    return ''
  }
  const itemNodeWithLocation: { businesses: { [key: string]: Array<string> }; _id: string; fullNameNode: string; location?: any } | undefined = itemNode.find(
    (node: { businesses: { [key: string]: Array<string> }; _id: string; fullNameNode: string; nodeType: { [key: string]: any } }) => {
      return node.nodeType?._id === itemSetPosition.nodeType
    }
  )
  if (!itemNodeWithLocation) {
    warningLocation.visible = true
    warningLocation.label = `Địa điểm ${item.title || ''} không thuộc loại điểm ${itemSetPosition.nodeTypeName || ''}`
    return ''
  }
  const quoteEqual = checkQuoteEqual(itemNodeWithLocation, positions.value[itemIndexSetPosition])
  if (quoteEqual) {
    warningLocation.visible = true
    warningLocation.label = `Địa điểm ${item.title || ''} không phù hợp khu vực được chọn`
    return ''
  }

  const businessAndTask = checkBusinessAndTask(itemNodeWithLocation, positions.value[itemIndexSetPosition])
  if (businessAndTask) {
    warningLocation.visible = true
    warningLocation.label = 'Nghiệp vụ tác vụ không hợp lệ'
    return ''
  }

  positions.value[itemIndexSetPosition].node = itemNodeWithLocation._id
  positions.value[itemIndexSetPosition].fullNameNode = itemNodeWithLocation.fullNameNode
  handelChangeSelectNode(itemIndexSetPosition, item?.data?._id)
}

const handleChoosePosition = async (data: any) => {
  const geocoder = new google.maps.Geocoder()
  const placeInfo = await geocoder.geocode({ placeId: data.place_id })

  if (placeInfo && Array.isArray(placeInfo.results) && placeInfo.results.length > 0) {
    nodePosition.lat = placeInfo.results[0].geometry.location.lat()
    nodePosition.lng = placeInfo.results[0].geometry.location.lng()
    nodePosition.placeId = data.place_id
  }
}

const handelChangeSelectNode = async (indexRoute: number, val: Array<{ [key: string]: any }> | any) => {
  let location = ''
  if (Array.isArray(val) && val.length && typeof val[0].location === 'string') {
    location = val[0].location
  }
  if (typeof val === 'string') {
    location = val
  }

  loadingSelectNode.value = true
  const nodeTheSame: Array<number> | undefined = getNodeTheSamePosition(indexRoute, $disableReactive(theSameItems.value))
  if ((Array.isArray(val) && val.length && typeof val[0].location === 'string') || typeof val === 'string') {
    let dataPosition: { [key: string]: any } = {}
    const api = $urlSystem.node.location.detail
    const res = await $api(api, { uriParams: { ID: location } })
    const resData: any = res?.data?.value
    if (resData?.success) {
      dataPosition = resData.data
    }

    if (dataPosition) {
      positions.value[indexRoute].lat = val[0]?.locationData?.[0]?.lat || dataPosition.lat
      positions.value[indexRoute].lng = val[0]?.locationData?.[0]?.lng || dataPosition.lng
      if (Array.isArray(nodeTheSame)) {
        nodeTheSame.forEach((numberRoute: number) => {
          if (numberRoute !== indexRoute) {
            if (Array.isArray(dataPosition.nodeDetail) && dataPosition.nodeDetail.length) {
              let findItemPosition = dataPosition.nodeDetail.find((itemNode: { [key: string]: any }) => {
                return itemNode.nodeType && itemNode.nodeType.name === positions.value[indexRoute].nodeTypeName
              })
              const businessAndTask = checkBusinessAndTask(findItemPosition, positions.value[numberRoute])
              if (businessAndTask) {
                findItemPosition = undefined
              }
              positions.value[numberRoute].node = findItemPosition?._id || ''
              positions.value[numberRoute].fullNameNode = findItemPosition?.fullNameNode || ''
              positions.value[numberRoute].lat = val[0]?.locationData?.[0]?.lat || dataPosition.lat
              positions.value[numberRoute].lng = val[0]?.locationData?.[0]?.lng || dataPosition.lng
            }
          }
        })
      }
    }
  } else {
    positions.value[indexRoute].lat = ''
    positions.value[indexRoute].lng = ''

    nodeTheSame &&
      nodeTheSame.forEach((numberRoute: number) => {
        if (numberRoute !== indexRoute) {
          positions.value[numberRoute].node = ''
          positions.value[numberRoute].fullNameNode = ''
          positions.value[numberRoute].lat = ''
          positions.value[numberRoute].lng = ''
        }
      })
  }

  setRoutingPositions()
  createRoute($disableReactive(positions.value))
  loadingSelectNode.value = false
}
const getNodeTheSamePosition = (index: number, data: Array<Array<number>>) => {
  if (typeof index !== 'number' || !Array.isArray(data)) {
    return
  }
  let result: Array<number> = []
  const filteredItems = data.filter((i: Array<number>) => i.includes(index))
  const tempData = data.filter((i: Array<number>) => !i.includes(index))
  filteredItems.forEach((item) => {
    if (!Array.isArray(item) || item.length !== 2) {
      return
    }
    const tempIndex = item.findIndex((o) => o === index)
    let tempIndex2
    if (tempIndex === 0) {
      tempIndex2 = 1
    } else {
      tempIndex2 = 0
    }

    result.push(item[1])
    result.push(item[0])

    const itemNodeTheSames: Array<number> | undefined = getNodeTheSamePosition(item[tempIndex2], tempData)
    if (Array.isArray(itemNodeTheSames)) {
      result = result.concat(itemNodeTheSames)
    }
  })
  return $lodash.uniq(result)
}

const emits = defineEmits(['applyPosition'])

const submit = () => {
  emits('applyPosition', positions.value)
  visible.value = false
}

defineExpose({
  open
})

const getKeyLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'name'
  }
  return 'addressByType'
}

const getKeySubLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'address'
  }
  return 'address'
}

const keyAndValueQuoteEqual: { [KEY: string]: any } = {
  WARD: 'areaLevel3',
  DISTRICT: 'areaLevel2',
  CITY: 'areaLevel1'
}

const checkBusinessAndTask = (node: { businesses: { [key: string]: Array<string> } }, route: { [key: string]: any }): boolean => {
  if (!route.businesses || !Object.keys(route.businesses).length) {
    return false
  }
  return Object.keys(route.businesses).some((item: string) => {
    if (!node || !node.businesses || !Object.keys(node.businesses).length || !node?.businesses[item]) {
      return true
    }
    return route.businesses[item].some((task: string) => {
      return !node?.businesses[item].includes(task)
    })
  })
}
const checkQuoteEqual = (node: { [key: string]: any }, route: { [key: string]: any }) => {
  if (!route.quoteEqual || typeof route.quoteEqual !== 'string' || route.quoteEqual === 'NODE') {
    return false
  }
  if (
    !node.location ||
    !node.location?.addressDetail ||
    !node.location?.addressDetail[keyAndValueQuoteEqual[route.quoteEqual]] ||
    !node.location?.addressDetail[keyAndValueQuoteEqual[route.quoteEqual]].formatted_address
  ) {
    return true
  }
}
</script>
